import { useState } from 'react';
import { ChevronDown, ChevronRight, Snowflake, RotateCcw, Download } from 'lucide-react';
import PeriodPicker from '../components/intercompany/PeriodPicker.jsx';
import StatusBadge from '../components/intercompany/StatusBadge.jsx';
import RepartitionSection from '../components/intercompany/RepartitionSection.jsx';
import ProjectedEventsSection from '../components/intercompany/ProjectedEventsSection.jsx';
import ServicesSection from '../components/intercompany/ServicesSection.jsx';
import RecapTable from '../components/intercompany/RecapTable.jsx';
import Modal from '../components/Modal.jsx';
import LoadingSpinner from '../components/LoadingSpinner.jsx';
import {
  useIntercompanyPeriod,
  useCrystallizePeriod,
  useDecrystallizePeriod,
} from '../hooks/useIntercompany.js';
import { useEntitiesAdmin } from '../hooks/useAdminEntities.js';
import { useCategories } from '../hooks/useCategories.js';
import { formatCurrency } from '../lib/format.js';

// ── Helpers ───────────────────────────────────────────────────────────────────

function currentYYYYMM() {
  const now = new Date();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  return `${now.getFullYear()}-${mm}`;
}

function formatPeriodLabel(yyyymm) {
  if (!yyyymm) return '';
  const [year, month] = yyyymm.split('-');
  const d = new Date(Number(year), Number(month) - 1, 1);
  return new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' }).format(d);
}

// ── Collapsible section wrapper ───────────────────────────────────────────────

function Section({ title, defaultOpen = true, badge, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 sm:px-5 hover:bg-slate-50 transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-400 focus-visible:outline-none"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-900">{title}</span>
          {badge != null && (
            <span className="text-xs rounded-full bg-slate-100 px-2 py-0.5 text-slate-500 font-medium">
              {badge}
            </span>
          )}
        </div>
        {open ? (
          <ChevronDown size={16} className="text-slate-400" aria-hidden />
        ) : (
          <ChevronRight size={16} className="text-slate-400" aria-hidden />
        )}
      </button>
      {open && <div className="px-4 pb-4 sm:px-5">{children}</div>}
    </div>
  );
}

// ── Confirmation modal ────────────────────────────────────────────────────────

function ConfirmModal({ open, onClose, onConfirm, title, description, confirmLabel, isPending }) {
  return (
    <Modal open={open} onClose={onClose} title={title} size="sm">
      <p className="text-sm text-slate-600 mb-5">{description}</p>
      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          Annuler
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={isPending}
          className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
        >
          {isPending ? 'Traitement…' : confirmLabel}
        </button>
      </div>
    </Modal>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function IntercompanyPage() {
  const [yyyymm, setYyyyMm] = useState(currentYYYYMM);
  const [decrystallizeModalOpen, setDecrystallizeModalOpen] = useState(false);

  const { data, isLoading, error } = useIntercompanyPeriod(yyyymm);
  const { data: entitiesData } = useEntitiesAdmin();
  const { data: categoriesData } = useCategories();

  const crystallizeMutation = useCrystallizePeriod();
  const decrystallizeMutation = useDecrystallizePeriod();

  // Build entity lookup map: id → { id, slug, name }
  const entities = Array.isArray(entitiesData)
    ? entitiesData
    : entitiesData?.entities ?? [];
  const entityMap = Object.fromEntries(entities.map((e) => [e.id, e]));

  const categories = Array.isArray(categoriesData)
    ? categoriesData
    : categoriesData?.categories ?? [];

  const period = data?.period ?? null;
  const lines = data?.lines ?? [];
  const recapByEntity = data?.recap_by_entity ?? [];
  const projectedEvents = data?.projected_events ?? [];

  const status = period?.status ?? 'draft';

  const repartitionLines = lines.filter((l) => l.type === 'repartition');
  const serviceCount = lines.filter((l) => l.type === 'service').length;

  const totalTtc = period?.total_ttc_cents ?? 0;
  const lineCount = period?.line_count ?? lines.length;

  // Bouton Cristalliser : activé si au moins 1 event projeté OU 1 service line
  const canCrystallize = projectedEvents.length > 0 || serviceCount > 0;

  const anyMutating = crystallizeMutation.isPending || decrystallizeMutation.isPending;

  async function handleCrystallize() {
    await crystallizeMutation.mutateAsync(yyyymm);
  }

  async function handleDecrystallizeConfirm() {
    await decrystallizeMutation.mutateAsync(yyyymm);
    setDecrystallizeModalOpen(false);
  }

  const mutationError = crystallizeMutation.error ?? decrystallizeMutation.error;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-lg font-semibold text-slate-900">Refacturation intra-groupe</h1>
        <p className="text-sm text-slate-500">{formatPeriodLabel(yyyymm)}</p>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-3 sm:px-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          {/* Left: period picker + status */}
          <div className="flex items-center gap-3 flex-wrap">
            <PeriodPicker value={yyyymm} onChange={setYyyyMm} />
            {period && (
              <StatusBadge
                status={status}
                validatedAt={period.validated_at}
                validatedBy={period.validated_by}
              />
            )}
          </div>

          {/* Right: action buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* Cristalliser — visible si status='draft' ou période absente */}
            {status !== 'validated' && (
              <button
                type="button"
                onClick={handleCrystallize}
                disabled={anyMutating || !canCrystallize}
                title={
                  !canCrystallize
                    ? 'Aucun élément à cristalliser — ajoute une prévisionnelle avec refacturation ou une prestation'
                    : undefined
                }
                className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Snowflake size={14} aria-hidden />
                {crystallizeMutation.isPending ? 'Cristallisation…' : 'Cristalliser le mois'}
              </button>
            )}

            {/* Décristalliser — visible si status='validated' */}
            {status === 'validated' && (
              <button
                type="button"
                onClick={() => setDecrystallizeModalOpen(true)}
                disabled={anyMutating}
                className="flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-700 hover:bg-amber-100 disabled:opacity-50 transition-colors"
              >
                <RotateCcw size={14} aria-hidden />
                Décristalliser
              </button>
            )}

            {/* Exporter */}
            <button
              type="button"
              disabled
              className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-400 cursor-not-allowed"
              aria-label="Export (à venir)"
              title="Export disponible prochainement"
            >
              <Download size={14} aria-hidden />
              Exporter
            </button>
          </div>
        </div>

        {/* Mutation error feedback */}
        {mutationError && (
          <p className="mt-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
            {mutationError.message}
          </p>
        )}
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      )}

      {/* API error */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          Impossible de charger la période : {error.message}
        </div>
      )}

      {!isLoading && (
        <>
          {/* 1. Récapitulatif par entité */}
          <Section title="Récapitulatif par entité" defaultOpen badge={recapByEntity.length || undefined}>
            {period && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-3">
                  <p className="text-xs text-slate-500 mb-0.5">Total TTC période</p>
                  <p className="text-base font-semibold tabular-nums text-slate-900">
                    {formatCurrency(totalTtc)}
                  </p>
                </div>
                <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-3">
                  <p className="text-xs text-slate-500 mb-0.5">Nombre de lignes</p>
                  <p className="text-base font-semibold tabular-nums text-slate-900">{lineCount}</p>
                </div>
              </div>
            )}
            <RecapTable recapByEntity={recapByEntity} entityMap={entityMap} />
          </Section>

          {/* 2. À cristalliser — uniquement si status='draft' (période non cristallisée) */}
          {status !== 'validated' && (
            <Section
              title="À cristalliser"
              defaultOpen
              badge={projectedEvents.length > 0 ? projectedEvents.length : undefined}
            >
              <ProjectedEventsSection
                projectedEvents={projectedEvents}
                entityMap={entityMap}
                categories={categories}
              />
            </Section>
          )}

          {/* 3. Refacturations cristallisées — uniquement si status='validated' */}
          {status === 'validated' && (
            <Section
              title="Refacturations cristallisées"
              defaultOpen
              badge={repartitionLines.length > 0 ? repartitionLines.length : undefined}
            >
              <RepartitionSection lines={lines} entityMap={entityMap} />
            </Section>
          )}

          {/* 4. Services rendus — toujours visible */}
          <Section
            title="Services rendus"
            defaultOpen
            badge={serviceCount > 0 ? serviceCount : undefined}
          >
            <ServicesSection
              lines={lines}
              entityMap={entityMap}
              entities={entities}
              yyyymm={yyyymm}
              isValidated={status === 'validated'}
            />
          </Section>

          {/* Footer légal */}
          <p className="text-xs text-slate-400 text-center px-4 pb-2">
            Exonération art. 261 B CGI sur les partages de frais — les prestations de service sont soumises à TVA 20 %.
          </p>
        </>
      )}

      {/* Confirmation décristallisation */}
      <ConfirmModal
        open={decrystallizeModalOpen}
        onClose={() => setDecrystallizeModalOpen(false)}
        onConfirm={handleDecrystallizeConfirm}
        title="Décristalliser la période"
        description="Cela supprimera les écritures comptables et les évènements cash forecast générés par la cristallisation. La projection live reprendra pour ce mois. Les prestations de service saisies manuellement seront conservées."
        confirmLabel="Décristalliser"
        isPending={decrystallizeMutation.isPending}
      />
    </div>
  );
}
